import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import type { Session } from "@/shared/types/session.type"
import type { Profile } from "@/features/auth/types/auth-actions.type"

/** Shown when sidebar / mobile nav links are disabled until Profile required fields are completed. */
export const PROFILE_NAV_LOCK_TOOLTIP = "Complete your Profile to continue"

/**
 * Paths incomplete volunteers/homeowners may visit while currentStep === 1.
 * Everything else under the dashboard should redirect to /profile.
 */
export function isAllowedPathDuringProfileLock(pathname: string): boolean {
    return pathname === "/profile" || pathname.startsWith("/profile/")
}

/**
 * Whether a volunteer or homeowner must finish Profile (currentStep === 1)
 * before using the rest of the app.
 */
export function isNavLockedUntilProfileComplete(
    session: Session | null | undefined,
    profile: Profile | null | undefined,
    isLoading = false,
): boolean {
    if (isLoading) return false
    if (!session?.sub) return false
    if (session.role !== UserRole.VOLUNTEER && session.role !== UserRole.HOMEOWNER) {
        return false
    }
    if (!profile) return false
    return profile.currentStep === 1
}

/**
 * True when a volunteer or homeowner must finish Profile (currentStep === 1) before using the rest of the app.
 * Uses SessionContext profile, which is refreshed after successful profile saves.
 */
export function useNavLockedUntilProfileComplete(): boolean {
    const { session, profile, isLoading } = useSession()
    return isNavLockedUntilProfileComplete(session, profile, isLoading)
}
